'use client';

import { useFormState } from 'react-dom';
import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';

import Alert from '@referralexchange/rea-ui-react/src/atoms/Alert';
import Input from '@referralexchange/rea-ui-react/src/atoms/Input';
import Password from '@referralexchange/rea-ui-react/src/components/inputs/Password';
import Checkbox from '@referralexchange/rea-ui-react/src/atoms/Checkbox';
import Grid from '@referralexchange/rea-ui-react/src/components/containers/Grid';
import Content from '@referralexchange/rea-ui-react/src/atoms/Content';
import Button from '@referralexchange/rea-ui-react/src/atoms/Button';
import '@referralexchange/rea-ui-react/src/Utils/css';

import auth from '~/config/auth';
import { useAnalytics } from '~/contexts/AnalyticsContext.tsx';
import styles from './form.module.scss';

import { signin } from './actions';

const initialState = {
  message: '',
  alert: '',
  errors: {},
  formData: {},
};

export default function Form() {
  const [state, formAction] = useFormState(signin, initialState);
  const [loading, setLoading] = useState(false);
  const [clientError, setClientError] = useState('');
  const { reaa } = useAnalytics();
  const submitAttempted = useRef(false);
  const emailRef = useRef('');
  const passwordRef = useRef('');
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!submitAttempted.current) return;
    setLoading(false);

    if (state.alert === 'error') {
      reaa('signin_event', 'error', { message: state.message });
    } else {
      reaa('signin_event', 'success', {});
      window.location.replace(state.redirectUrl);
    }
  }, [state, reaa]);

  const handleSubmit = (e) => {
    const formData = new FormData(e.currentTarget);
    emailRef.current = formData.get('email');
    passwordRef.current = formData.get('password');
    setClientError('');
    setLoading(true);
    submitAttempted.current = true;
    reaa('signin_event', 'attempted', {});
  };

  const handleForgotPassword = () => {
    reaa('signin_event', 'forgot_password_click', {});
  };

  const handleRememberMeToggle = (e) => {
    reaa('signin_event', 'remember_me_toggle', { checked: e.target.checked });
  };

  const handleInvalid = (e) => {
    const message = e.target.validity.typeMismatch
      ? 'Please enter a valid email address.'
      : 'Please fill in all required fields.';
    setClientError(message);
    reaa('signin_event', 'client_error', { message, field: e.target.name });
  };

  const errorMessage = clientError || (state.alert === 'error' ? state.message : '');

  return (
    <form action={formAction} onSubmit={handleSubmit}>
      <input type="hidden" name="prevUrl" value={searchParams.get('prev') || ''} />
      <Grid>
        {errorMessage && (
          <Alert className={Grid.styles.fullWidth} animation="fade-down-in" type="error">
            <strong>Error</strong>
            {' '}
            {errorMessage}
          </Alert>
        )}
        <Input className={Grid.styles.fullWidth} label="Email" name="email" type="email" required onInvalid={handleInvalid} />
        <Password className={Grid.styles.fullWidth} label="Password" name="password" required onInvalid={handleInvalid} />
        <div className={Grid.styles.leftAligned}>
          <Checkbox name="remember-me" defaultChecked onChange={handleRememberMeToggle}>
            <Content>Remember me</Content>
          </Checkbox>
        </div>
        <div className={[Grid.styles.rightAligned, styles['forgot-password']].css()}>
          <Button
            href={auth.forgotPassword}
            textButton
            onClick={handleForgotPassword}
          >
            Forgot your password?
          </Button>
        </div>
        <div className={[Grid.styles.centerAligned, Grid.styles.fullWidth].css()}>
          <Button loading={loading} disabled={loading} type="submit">Sign In</Button>
        </div>
      </Grid>
    </form>
  );
}
